/**
 * /admin/customer/:phone — Customer 360° view.
 *
 * Single-page rollup of every touchpoint we have for a phone number:
 *  - Identity card
 *  - Lifetime touchpoint counter
 *  - Leads (with status + verification badges)
 *  - Applications
 *  - Eligibility checks
 *  - Payments (Stripe + manual UPI)
 *  - SMS deliveries (MSG91)
 *  - Grievances
 *  - Follow-ups
 *
 * Also supports a search-first landing at /admin/customer (no phone param).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  Mail,
  Search,
  Loader2,
  User,
  Inbox,
  FileText,
  ClipboardCheck,
  IndianRupee,
  MessageSquare,
  MessageCircle,
  Calendar,
  ShieldCheck,
  XCircle,
  Hourglass,
  Layers,
  ExternalLink,
} from "lucide-react";
import { Customer360, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Customer360Page() {
  const nav = useNavigate();
  const { phone } = useParams();

  useEffect(() => {
    if (!tokenStorage.get()) nav("/admin", { replace: true });
  }, [nav]);

  if (!phone) return <SearchLanding />;
  return <CustomerDetail phone={phone} />;
}

// ---------------------------------------------------------------------------
// Search landing: /admin/customer
// ---------------------------------------------------------------------------
function SearchLanding() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mergeSel, setMergeSel] = useState([]); // array of {id, phone, name}
  const [mergeModal, setMergeModal] = useState(null); // {source, target}

  const search = useCallback(async (term) => {
    if (!term || term.length < 2) { setRows([]); return; }
    setBusy(true);
    try {
      const d = await Customer360.search(term);
      setRows(d.items || []);
    } catch (_) {
      setRows([]);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [q, search]);

  function toggleSel(row) {
    const has = mergeSel.some((s) => s.id === row.id);
    if (has) setMergeSel(mergeSel.filter((s) => s.id !== row.id));
    else if (mergeSel.length < 2) setMergeSel([...mergeSel, row]);
  }

  return (
    <Shell>
      <div className="max-w-2xl mx-auto">
        <h1 className="font-display text-3xl md:text-4xl text-center">Customer 360°</h1>
        <p className="mt-2 text-white/70 text-sm text-center">
          Find a customer by name, phone number or email — see every touchpoint on one screen.
          Select 2 rows to <span className="text-[#F0D788]">merge duplicates</span> into a canonical phone.
        </p>

        <div className="mt-8 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="c360-search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a name, phone or email…"
            className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white/[0.05] border border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none text-white text-sm"
          />
          {busy && <Loader2 size={14} className="animate-spin absolute right-4 top-1/2 -translate-y-1/2 text-white/50" />}
        </div>

        {mergeSel.length > 0 && (
          <div data-testid="c360-merge-bar" className="mt-4 rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/[0.08] px-4 py-3 flex items-center justify-between text-sm">
            <div className="text-white/85">
              <span className="text-[#F0D788] font-semibold">{mergeSel.length}</span> selected for merge
              {mergeSel.length === 2 && (
                <span className="text-white/60 ml-2 text-xs">
                  · {mergeSel[0].phone} → {mergeSel[1].phone}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                data-testid="c360-merge-clear"
                onClick={() => setMergeSel([])}
                className="px-3 py-1 rounded-full text-xs text-white/60 hover:text-white"
              >Clear</button>
              <button
                data-testid="c360-merge-btn"
                disabled={mergeSel.length !== 2}
                onClick={() => setMergeModal({ source: mergeSel[0], target: mergeSel[1] })}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full btn-gold text-xs font-semibold disabled:opacity-50"
              >
                Merge into second →
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2" data-testid="c360-search-results">
          {rows.map((r) => {
            const selected = mergeSel.some((s) => s.id === r.id);
            return (
              <div
                key={r.id}
                data-testid={`c360-search-hit-${r.id}`}
                className={`w-full rounded-2xl border ${selected ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]" : "border-white/10 bg-[#0A1F44] hover:border-[#D4AF37]/40"} px-4 py-3 flex items-center gap-3`}
              >
                <input
                  type="checkbox"
                  data-testid={`c360-merge-select-${r.id}`}
                  checked={selected}
                  onChange={() => toggleSel(r)}
                  className="w-4 h-4 accent-[#D4AF37]"
                />
                <button onClick={() => nav(`/admin/customer/${encodeURIComponent(r.phone)}`)} className="flex-1 text-left flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 grid place-items-center">
                    <User size={14} className="text-[#F0D788]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{r.name}</div>
                    <div className="text-xs text-white/55 truncate">{r.phone}{r.email ? ` · ${r.email}` : ""}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#F0D788]">{r.interest}</div>
                </button>
              </div>
            );
          })}
          {!busy && q.length >= 2 && rows.length === 0 && (
            <div className="text-center text-white/50 text-sm py-8">No matches.</div>
          )}
        </div>
      </div>

      {mergeModal && (
        <MergeCustomerModal
          source={mergeModal.source}
          target={mergeModal.target}
          onClose={() => setMergeModal(null)}
          onDone={() => { setMergeModal(null); setMergeSel([]); search(q); }}
        />
      )}
    </Shell>
  );
}

function MergeCustomerModal({ source, target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const requiredText = `MERGE ${source.phone}`;

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await Customer360.merge(source.phone, target.phone, reason.trim() || null);
      setSummary(r);
      setTimeout(onDone, 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="c360-merge-modal" className="w-full max-w-md rounded-3xl bg-[#0A1F44] border border-[#D4AF37]/30 shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6">
          <div className="font-display text-lg text-white">Merge customer records</div>
          <button onClick={onClose} className="text-white/50 hover:text-white">✕</button>
        </div>
        <div className="p-6">
          <div className="rounded-xl bg-black/25 border border-white/10 p-3 text-sm mb-3">
            <div className="text-white/60 text-xs">Fold this…</div>
            <div className="text-white font-medium">{source.name} · {source.phone}</div>
          </div>
          <div className="text-center text-[#F0D788] text-lg">↓</div>
          <div className="rounded-xl bg-[#D4AF37]/[0.06] border border-[#D4AF37]/40 p-3 text-sm mt-1 mb-4">
            <div className="text-[#F0D788] text-xs">…into this canonical record</div>
            <div className="text-white font-medium">{target.name} · {target.phone}</div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">Reason (audit log)</div>
              <input
                data-testid="c360-merge-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Duplicate — same customer via 2 phones"
                className="w-full px-3 py-2 rounded-lg bg-[#04102B] border border-white/15 text-white text-sm"
              />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/60 mb-1">
                Type <span className="text-red-300 font-mono">{requiredText}</span> to confirm
              </div>
              <input
                data-testid="c360-merge-confirm"
                required
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#04102B] border border-red-500/30 text-white text-sm font-mono"
              />
            </label>
            {err && <div className="text-xs text-red-200">{err}</div>}
            {summary && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-xs text-emerald-100">
                Merged. Rewrote: leads={summary.counts.leads}, apps={summary.counts.applications}, elig={summary.counts.eligibility_checks}, sms={summary.counts.sms_notifications}.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75">Cancel</button>
              <button
                type="submit"
                data-testid="c360-merge-submit"
                disabled={busy || confirmText !== requiredText}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-50"
              >
                {busy ? "Merging…" : "Merge records"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer detail: /admin/customer/:phone
// ---------------------------------------------------------------------------
function CustomerDetail({ phone }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Customer360.fetch(phone)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load"));
  }, [phone]);

  if (error) return <Shell><div className="py-16 text-center text-red-200">{error}</div></Shell>;
  if (!data) return <Shell><div className="py-16 text-center text-white/60">Loading customer…</div></Shell>;

  return (
    <Shell showBackToSearch>
      {/* Identity card */}
      <section
        data-testid="c360-identity"
        className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-6 md:p-8"
      >
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 grid place-items-center flex-shrink-0">
            <User size={24} className="text-[#F0D788]" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <h1 className="font-display text-3xl">{data.customer.name}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {data.customer.phone}</span>
              {data.customer.email && <span className="inline-flex items-center gap-1.5"><Mail size={13} /> {data.customer.email}</span>}
            </div>
          </div>
          <div className="rounded-2xl bg-black/25 border border-[#D4AF37]/20 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">Lifetime touchpoints</div>
            <div className="mt-1 font-display text-2xl text-[#F0D788]" data-testid="c360-touchpoint-count">{data.customer.lifetime_touchpoints}</div>
          </div>
        </div>
      </section>

      {/* Counters strip */}
      <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <CountCard testId="c360-count-leads" icon={Inbox} label="Leads" value={data.leads.length} />
        <CountCard testId="c360-count-applications" icon={FileText} label="Applications" value={data.applications.length} />
        <CountCard testId="c360-count-eligibility" icon={ClipboardCheck} label="Eligibility checks" value={data.eligibility.length} />
        <CountCard testId="c360-count-payments" icon={IndianRupee} label="Payments" value={data.payment_transactions.length + data.manual_payments.length} />
      </section>

      {/* Two-column detail */}
      <section className="mt-6 grid md:grid-cols-2 gap-5">
        {/* Leads */}
        <Panel testId="c360-panel-leads" title="Leads" icon={Inbox} count={data.leads.length}>
          {data.leads.length === 0 && <Empty>No leads for this customer.</Empty>}
          {data.leads.map((l) => (
            <div key={l.id} className="rounded-xl bg-black/25 border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <div className="text-white font-medium">{l.interest}</div>
                <VerifBadge v={l.verification_status} />
              </div>
              <div className="mt-1 text-[11px] text-white/55">
                {l.status?.toUpperCase()} · {formatDate(l.created_at)}
              </div>
              {l.message && <div className="mt-1 text-xs text-white/70 line-clamp-2">{l.message}</div>}
            </div>
          ))}
        </Panel>

        {/* Applications */}
        <Panel testId="c360-panel-applications" title="Applications" icon={FileText} count={data.applications.length}>
          {data.applications.length === 0 && <Empty>No applications submitted.</Empty>}
          {data.applications.map((a) => (
            <div key={a.id} className="rounded-xl bg-black/25 border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <div className="text-white font-medium">{a.type || "Application"}</div>
                <span className="text-[10px] uppercase tracking-wider text-[#F0D788]">{a.status}</span>
              </div>
              <div className="mt-1 text-[11px] text-white/55">
                {formatDate(a.created_at)} · Payment: {a.payment_status || "—"}
              </div>
            </div>
          ))}
        </Panel>

        {/* Eligibility */}
        <Panel testId="c360-panel-eligibility" title="Eligibility Checks" icon={ClipboardCheck} count={data.eligibility.length}>
          {data.eligibility.length === 0 && <Empty>No eligibility checks yet.</Empty>}
          {data.eligibility.map((e) => (
            <div key={e.id} className="rounded-xl bg-black/25 border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <div className="text-white font-medium">{e.loan_type_label || e.loan_type}</div>
                <span className="text-[10px] uppercase tracking-wider text-[#F0D788]">₹{Number(e.monthly_income||0).toLocaleString()}/mo</span>
              </div>
              <div className="mt-1 text-[11px] text-white/55">
                {formatDate(e.created_at)} · Payment: {e.payment_status || "—"}
              </div>
            </div>
          ))}
        </Panel>

        {/* Payments */}
        <Panel testId="c360-panel-payments" title="Payments" icon={IndianRupee} count={data.payment_transactions.length + data.manual_payments.length}>
          {data.payment_transactions.length === 0 && data.manual_payments.length === 0 && <Empty>No payments recorded.</Empty>}
          {data.payment_transactions.map((p) => (
            <div key={p.id} className="rounded-xl bg-black/25 border border-white/10 p-3 flex items-center justify-between">
              <div>
                <div className="text-white text-sm">₹ {Number(p.amount || 0).toLocaleString()} <span className="text-white/40">{p.currency}</span></div>
                <div className="text-[11px] text-white/55">{formatDate(p.created_at)} · Stripe · {p.payment_status}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/30" : "bg-white/10 text-white/70 border border-white/15"
              }`}>{p.payment_status}</span>
            </div>
          ))}
          {data.manual_payments.map((m) => (
            <div key={m.id} className="rounded-xl bg-amber-500/[0.06] border border-amber-400/20 p-3 flex items-center justify-between">
              <div>
                <div className="text-white text-sm">₹ {Number(m.amount || 0).toLocaleString()} <span className="text-white/40">manual</span></div>
                <div className="text-[11px] text-white/55">{formatDate(m.created_at)} · UPI · {m.status}</div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-100 border border-amber-400/40">{m.status}</span>
            </div>
          ))}
        </Panel>

        {/* SMS */}
        <Panel testId="c360-panel-sms" title="SMS · MSG91" icon={MessageSquare} count={data.sms.length}>
          {data.sms.length === 0 && <Empty>No SMS sent to this customer.</Empty>}
          {data.sms.map((s) => (
            <div key={s.id} className="rounded-xl bg-black/25 border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-[#F0D788]">{s.kind || "notification"}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  s.status === "delivered" ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/30" :
                  s.status === "failed" ? "bg-red-500/15 text-red-200 border border-red-400/30" :
                  "bg-white/10 text-white/70 border border-white/15"
                }`}>{s.status}</span>
              </div>
              <div className="mt-1 text-xs text-white/85 line-clamp-2">{s.message}</div>
              <div className="mt-1 text-[11px] text-white/50">{formatDate(s.created_at)}</div>
            </div>
          ))}
        </Panel>

        {/* Grievances */}
        <Panel testId="c360-panel-grievances" title="Grievances" icon={MessageCircle} count={data.grievances.length}>
          {data.grievances.length === 0 && <Empty>No grievances filed.</Empty>}
          {data.grievances.slice(0, 8).map((g) => (
            <div key={g.id} className="rounded-xl bg-black/25 border border-white/10 p-3">
              <div className="flex items-center justify-between">
                <div className="text-white text-sm">{g.subject || g.category || "Grievance"}</div>
                <span className="text-[10px] uppercase tracking-wider text-[#F0D788]">{g.status || "open"}</span>
              </div>
              <div className="mt-1 text-[11px] text-white/55">{formatDate(g.created_at)}</div>
            </div>
          ))}
        </Panel>
      </section>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------
function Shell({ children, showBackToSearch }) {
  const nav = useNavigate();
  return (
    <div data-testid="c360-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="border-b border-white/10 backdrop-blur-lg bg-black/20 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="c360-back"
            onClick={() => showBackToSearch ? nav("/admin/customer") : nav("/admin/leads")}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788]"
          >
            <ArrowLeft size={14} /> {showBackToSearch ? "Search customers" : "Back to Admin"}
          </button>
          <div className="flex items-center gap-3">
            <Logo size={40} testId="c360-logo" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Customer 360°</div>
          </div>
          <div />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">{children}</main>
    </div>
  );
}

function Panel({ title, icon: Icon, count, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-3xl border border-white/10 bg-[#0A1F44] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2 text-[#F0D788]">
          <Icon size={14} />
          <span className="text-[11px] tracking-[0.22em] uppercase">{title}</span>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/30 border border-white/10 text-white/80 font-mono">{count}</span>
      </div>
      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">{children}</div>
    </div>
  );
}

function CountCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/10 bg-[#0A1F44] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">{label}</div>
        <Icon size={14} className="text-[#F0D788]" />
      </div>
      <div className="mt-1.5 font-display text-2xl text-white">{value}</div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-center text-white/50 text-xs py-5">{children}</div>;
}

function VerifBadge({ v }) {
  const meta = {
    verified: { color: "text-emerald-300", Icon: ShieldCheck, label: "Verified" },
    rejected: { color: "text-red-300", Icon: XCircle, label: "Rejected" },
    pending:  { color: "text-amber-300", Icon: Hourglass, label: "Pending" },
  }[v || "pending"];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${meta.color}`}>
      <meta.Icon size={9} /> {meta.label}
    </span>
  );
}

function formatDate(iso) {
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || "—"; }
}
